import { Md5 } from 'ts-md5'
import { ProductHitAlgolia } from '../../../queries/algolia/ResultFetchAlgoliaByQuery.types'
import { getPriceFromProductHitAlgolia } from './price.utils'
import { SelectionItem } from '../../domain/selectionItem'
import { Cart } from '../../domain/cart'
import { GtmPageType } from '../../domain/gtm'
import { ProductImage } from '../../../.mesh'

export type GtmProduct = {
  item_name?: Product['gtm_name']
  item_id?: Product['sku']
  item_brand: 'Messika'
  price?: number | null
  item_variant?: string | (Variant | null)[]
  index?: number | string
  item_list_id?: string
  item_list_name?: string
  item_category?: string
  item_category1?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_category6?: string
}

export function createGtmProduct(
  product: Product | VariantProduct | GtmProduct | SelectionItem,
  params?: {
    position?: number | string
    listId?: string
    listName?: string
  },
): GtmProduct {
  const storedValues = JSON.parse(
    typeof window !== 'undefined' ? (window.localStorage.getItem('productValues') as any) : '{}',
  )
  const listId = params?.listId || (storedValues ? storedValues.listId : 'produit')
  const listName = params?.listName || (storedValues ? storedValues.listName : 'Produit')

  let productItem: GtmProduct = {
    item_brand: 'Messika',
    index: params?.position || '',
    item_variant: '',
    item_list_id: listId,
    item_list_name: listName,
  }
  const keys = Object.keys(product)
  for (const key of keys) {
    if (key.startsWith('item_') && product[key]) {
      const val = product[key]
      if (typeof val === 'string') {
        if (val.length > 0) {
          productItem[key] = val
        }
      } else {
        productItem[key] = val
      }
    }
  }
  if ('sku' in product) {
    productItem.item_name = product.gtm_name
    productItem.item_id = product.sku
    const priceRange: Product['priceRange'] =
      'price_range' in product ? product.price_range : product.priceRange
    productItem.price = priceRange?.minimum_price.regular_price.value
    const gtmCategoryObj = gtmCategory(product?.gtm_categories)
    productItem = { ...productItem, ...gtmCategoryObj }
  }
  if ('variants' in product) {
    const gtmCategoryObj = gtmCategory(product?.gtm_categories)
    productItem.item_variant = product.variants ?? ''
    productItem = { ...productItem, ...gtmCategoryObj }
  }
  return productItem
}

export function gtmProductPush(params: {
  product: GtmProduct | VariantProduct | Product
  event: string
  listId?: string
  listName?: string
  position?: number | string
}) {
  const { event, listId, listName, position } = params
  const product = createGtmProduct(params.product, {
    listName,
    listId,
    position,
  })
  const listIdValue = listId ?? 'categorie'
  const listNameValue = listName ?? "Catégorie d'Articles"

  gtmPush({
    event,
    ecommerce: {
      item_list_id: product.item_list_id,
      item_list_name: product.item_list_name,
      items: [
        {
          ...product,
        },
      ],
    },
  })

  const productValues = {
    position: position,
    listId: listIdValue,
    listName: listNameValue,
  }

  localStorage.setItem('productValues', JSON.stringify(productValues))
}

const gtmListeners = new Set<() => void>()

if (process.env.NODE_ENV === 'development') {
  gtmListeners.add(function () {
    if (window.dataLayer) {
      console.log(window.dataLayer.at(-1))
    }
  })
}

export function gtmPush(gtmEvent: any) {
  if (window.dataLayer) {
    window.dataLayer.push(gtmEvent)
    for (const listener of gtmListeners) {
      listener()
    }
  }
}

export function gtmPushCartEvent(options: {
  event:
    | 'begin_checkout'
    | 'view_gift_card'
    | 'view_shipping_address'
    | 'view_shipping_info'
    | 'add_shipping_info'
    | 'view_payment_info'
    | 'add_payment_info'
    /* eslint-disable-next-line */
    | (string & {})
  cart: Cart
  paymentType?: string
}) {
  const { event, cart, paymentType } = options
  const productItems: any[] = []

  cart.items.map((item) => {
    let obj = {}
    const product = item.product
    const gtmCategoryObj = gtmCategory(item.product.gtm_categories)
    const productSize = item?.configurable_options?.[0]?.value_label ?? null
    const sizeSku = productSize ? item?.product.sku + '-' + productSize : item?.product.sku

    const image = (product.media_gallery?.find((m) => {
      if (m?.__typename !== 'ProductImage') {
        return false
      }
      return m.roles?.includes('small_image')
    }) || product.media_gallery?.find((m) => m?.__typename === 'ProductImage')) as
      | ProductImage
      | undefined

    const itemPn = image?.url ? '/' + image?.url?.split('/').slice(-3).join('/') : ''
    const locale = window.location.pathname.slice(1).split('/').at(0) ?? "fr";


    obj['item_pn'] = itemPn
    obj['item_url_key'] = `/${locale}/${product.url_key}`;
    obj['item_name'] = item?.product.gtm_name
    obj['item_name_localized'] = item.product.name ?? '';
    obj['item_color'] = item.product.title ?? '';
    obj['item_id'] = item?.product.sku
    obj['price'] = item?.product.price_range.minimum_price.regular_price.value
    obj['item_brand'] = 'Messika'
    obj['index'] = item?.gtm_position ?? ''
    obj['quantity'] = item?.quantity
    obj['item_list_id'] = item.item_list_id ?? 'panier'
    obj['item_list_name'] = item.item_list_name ?? 'Panier'
    obj['item_variant'] = sizeSku
    obj['item_gender'] = ''
    switch (item.product.gender) {
      case 177:
        obj['item_gender'] = 'femme'
        break
      case 178:
        obj['item_gender'] = 'homme'
        break
      case 179:
        obj['item_gender'] = 'mixte'
        break
    }
    obj = { ...obj, ...gtmCategoryObj }

    productItems.push(obj)
  })

  const shippingAddress = cart.shipping_addresses.at(0)

  const gtmEvent = {
    event,
    ecommerce: {
      value: cart.prices.grand_total.value,
      currency: cart.prices.grand_total.currency,
      items: productItems,
    } as Record<string, any>,
  }

  switch (event) {
    case 'add_payment_info':
      gtmEvent.ecommerce.payment_type =
        paymentType ??
        cart?.selected_payment_method?.title ??
        cart?.available_payment_methods?.[0].title
      localStorage.setItem('gtm_payment_type', gtmEvent.ecommerce.payment_type)
      break

    case 'add_shipping_info':
      gtmEvent.ecommerce.shippingTier =
        shippingAddress?.available_shipping_methods?.at(0)?.carrier_title
      break

    default:
      break
  }

  gtmPush(gtmEvent)

  localStorage.setItem('cartProducts', JSON.stringify(productItems))
}

export function gtmCategory(item: string | undefined): {
  item_category?: string
  item_category1?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_category6?: string
} {
  const categorieProp = {}
  const categorie = item

  categorie
    ?.split('/')
    .filter((n) => n)
    .map((cat, index) => {
      index === 0
        ? (categorieProp[`item_category`] = cat)
        : (categorieProp[`item_category${index + 1}`] = cat)
    })
    .slice(0, 4)

  return categorieProp
}

export async function sha256(message: string | number | null | undefined) {
  if (message === null || message === undefined) {
    return ''
  }
  const msgBuffer = new TextEncoder().encode(`${message}`)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}

function hasPageView(pageType: GtmPageType) {
  if (!Array.isArray(window.dataLayer)) {
    return false
  }
  let lastPageViewEvent: {
    event: string
    page_type?: string
  } | null = null
  for (let i = window.dataLayer.length - 1; i >= 0; i -= 1) {
    const layer = window.dataLayer[i]
    if (layer.event === 'page_view') {
      lastPageViewEvent = layer
      break
    }
  }
  return lastPageViewEvent?.page_type === pageType
}

export async function waitForPageView(pageType: GtmPageType, aborted: { value: boolean }) {
  return new Promise<boolean>((resolve) => {
    if (aborted?.value) {
      resolve(false)
      return
    }
    if (hasPageView(pageType)) {
      resolve(true)
      return
    }
    const listener = () => {
      if (aborted?.value) {
        gtmListeners.delete(listener)
        resolve(false)
        return
      }
      if (hasPageView(pageType)) {
        gtmListeners.delete(listener)
        resolve(true)
      }
    }
    gtmListeners.add(listener)
  })
}

export function md5Encrypt(value: (string | number) | undefined | null) {
  return value ? Md5.hashStr(value?.toString()) : ''
}

type GtmTrackerEvents = {
  add: (ev: { product: ProductHitAlgolia | SelectionItem }) => void
}

export const GtmViewItemListTracker = {
  queue: [] as Record<string, any>[],
  skus: [] as string[],
  listeners: {
    add: [],
  } as Record<keyof GtmTrackerEvents, ((ev: any) => void)[]>,
  reset() {
    this.queue = []
    this.skus = []
    this.listeners = {
      add: [],
    }
  },
  addProductHitAlgolia(product: ProductHitAlgolia, meta?: Record<string, any>) {
    const sku = Array.isArray(product.sku) ? product.sku[0] : product.sku
    if (this.skus.includes(sku)) {
      return
    }
    const gtmItem = {
      ...meta,
      item_brand: 'Messika',
      item_name: product.gtm_name ?? product.name,
      item_id: sku,
      price: getPriceFromProductHitAlgolia(product),
      ...gtmCategory(product.categories.level0?.join('/')),
    }
    this.queue.push(gtmItem)
    this.skus.push(sku)
    this.listeners['add'].forEach((cb) => cb({ product }))
  },
  addSelectionItem(selectionItem: SelectionItem, meta?: Record<string, any>) {
    if (!selectionItem.sku || this.skus.includes(selectionItem.sku)) {
      return
    }
    this.queue.push({
      ...meta,
      item_brand: 'Messika',
      item_name: selectionItem.gtm_name || selectionItem.name || selectionItem.sku,
      item_id: selectionItem.sku,
      price: selectionItem.priceRange?.minimum_price.regular_price,
      ...gtmCategory(selectionItem.gtm_categories),
    })
    this.skus.push(selectionItem.sku)
    this.listeners['add'].forEach((cb) => cb({ product: selectionItem }))
  },
  addEventListener<Event extends keyof GtmTrackerEvents>(
    event: Event,
    fn: GtmTrackerEvents[Event],
  ) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(fn)
  },
  pushEvent(params: { item_list_name: string; item_list_id: string }) {
    if (this.queue.length === 0) {
      return
    }
    const gtmItems = this.queue.map((item) => {
      return {
        ...item,
        item_list_name: params.item_list_name,
        item_list_id: params.item_list_id,
      }
    })
    gtmPush({
      event: 'view_item_list',
      ecommerce: {
        item_list_name: params.item_list_name,
        item_list_id: params.item_list_id,
        items: gtmItems,
      },
    })
    this.queue = []
    this.skus = []
  },
}
